import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Crop, X, Eye, EyeOff, ArrowRight, Type, Edit2 } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { compressImageForDisplay } from "@/lib/imageCompression";
import { hapticSuccess } from "@/utils/haptics";
import { ImageCropDialog } from "@/components/ImageCropDialog";
import { type MetricDefinition } from "@/lib/metricDefinitions";
import { useMetricDefinitions } from "@/lib/useMetricDefinitions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { shareToDiscord } from "@/lib/discordShare";
import { MiniGraphOverlay } from "@/components/MiniGraphOverlay";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { getLocalizedMetricName } from "@/lib/utils";
import { withTimeout } from "@/lib/promiseUtils";

const CreateSnap = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isPremium } = useSubscriptionStatus(user?.id);
  const selectedDate = (location.state as any)?.selectedDate || new Date();
  const initialImage = (location.state as any)?.initialImage || null;
  const initialVideoUrl = (location.state as any)?.initialVideoUrl || null;
  const initialText = (location.state as any)?.initialText || "";
  const initialMetric = (location.state as any)?.selectedMetric || "";
  
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(initialVideoUrl);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [textContent, setTextContent] = useState(initialText);
  const [selectedMetric, setSelectedMetric] = useState<string>(initialMetric);
  const [isPosting, setIsPosting] = useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!user) {
    navigate("/auth");
    return null;
  }

  // Fetch user's enabled metrics
  const { data: metricPrefs, isLoading: isLoadingPrefs } = useQuery({
    queryKey: ["metric-preferences", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Fetch custom metrics
  const { data: customMetrics, isLoading: isLoadingCustom } = useQuery({
    queryKey: ["custom-metrics", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", user.id);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch follower count
  const { data: followerCount } = useQuery({
    queryKey: ["follower-count", user.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "get_follower_count",
        { p_user_id: user.id }
      );
      if (error) throw error;
      return data || 0;
    },
  });

  // Fetch user profile for is_public status
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_public")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Fetch today's progress snaps to check which metrics are already used
  const snapDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
  const { data: todaySnaps } = useQuery({
    queryKey: ["today-snaps", user.id, snapDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("progress_snaps")
        .select("metric_name")
        .eq("user_id", user.id)
        .eq("snap_date", snapDate);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch total progress snap count for free users (1 free snap total, not per month)
  const { data: totalSnapCount } = useQuery({
    queryKey: ["total-snap-count", user.id, isPremium],
    queryFn: async () => {
      // Only fetch for non-premium users
      if (isPremium) return 0;
      
      const { count, error } = await supabase
        .from("progress_snaps")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if (error) throw error;
      return count || 0;
    },
    enabled: !isPremium, // Only run for non-premium users
  });

  // Fetch metric definitions for built-in metrics
  const { data: metricDefinitionsObj, isLoading: isLoadingDefs, error: defsError } = useMetricDefinitions();
  
  const metricDefinitions = metricDefinitionsObj ? Object.values(metricDefinitionsObj) : [];
  
  if (defsError) {
    console.error('[CreateSnap] Query error:', defsError);
  }

  // Build list of available metrics
  const availableMetrics: Array<{ key: string; label: string; emoji: string }> = [];
  
  const metricsArray: MetricDefinition[] = Array.isArray(metricDefinitions) 
    ? metricDefinitions 
    : (metricDefinitions ? Object.values(metricDefinitions) as MetricDefinition[] : []);
  
  if (metricPrefs && metricsArray.length > 0) {
    const allEnabledKeys = [
      ...(metricPrefs.nutrition_metrics || []),
      ...(metricPrefs.activity_sleep_metrics || []),
      ...(metricPrefs.results_metrics || []),
    ];
    
    allEnabledKeys.forEach((key) => {
      const def = metricsArray.find((m) => m.key === key);
      if (def) {
        availableMetrics.push({
          key,
          label: getLocalizedMetricName(key, t, def.label),
          emoji: def.emoji,
        });
      }
    });
  }

  // Add custom metrics (no translation needed - user-defined names)
  if (customMetrics && Array.isArray(customMetrics)) {
    customMetrics.forEach((metric) => {
      availableMetrics.push({
        key: metric.metric_key,
        label: metric.metric_name,
        emoji: metric.metric_emoji,
      });
    });
  }

  // Mutation for updating profile visibility
  const updateVisibility = useMutation({
    mutationFn: async (isPublic: boolean) => {
      const { error } = await supabase
        .from("profiles_internal")
        .update({ is_public: isPublic })
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile", user.id] });
      toast.success(t('notifications.toast.profile_visibility_updated'));
      setShowVisibilityDialog(false);
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_update_visibility'));
    },
  });

  // Auto-select first metric NOT posted today (only if no initial metric was provided)
  useEffect(() => {
    if (availableMetrics.length > 0 && !selectedMetric && !initialMetric && todaySnaps) {
      const usedMetrics = new Set(todaySnaps.map(snap => snap.metric_name));
      const firstUnusedMetric = availableMetrics.find(m => !usedMetrics.has(m.key));
      if (firstUnusedMetric) {
        setSelectedMetric(firstUnusedMetric.key);
      } else {
        // If all metrics are used, just select the first one
        setSelectedMetric(availableMetrics[0].key);
      }
    }
  }, [availableMetrics.length, selectedMetric, initialMetric, todaySnaps]);

  // Handle initial image from QuickEntryInput with auto-crop
  useEffect(() => {
    if (initialImage && !previewUrl && !croppedImageBlob) {
      // Auto-crop the image with default settings
      autoCropImage(initialImage);
    }
  }, [initialImage, previewUrl, croppedImageBlob]);

  // Pick up pending video blob from window (set by camera overlay)
  useEffect(() => {
    if (initialVideoUrl && !videoBlob) {
      const pending = (window as any).__pendingSnapVideoBlob as Blob | undefined;
      if (pending) {
        setVideoBlob(pending);
        delete (window as any).__pendingSnapVideoBlob;
      }
    }
    // Cleanup blob URL on unmount
    return () => {
      if (videoPreviewUrl && videoPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, []);

  const autoCropImage = async (imageSrc: string) => {
    try {
      // Load the image to get its dimensions
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageSrc;
      });

      // Calculate center crop area with 9:16 aspect ratio (standard phone portrait)
      const aspectRatio = 9 / 16;
      const imgWidth = img.width;
      const imgHeight = img.height;
      const imgAspectRatio = imgWidth / imgHeight;

      let cropWidth: number;
      let cropHeight: number;

      if (imgAspectRatio > aspectRatio) {
        // Image is wider than target ratio
        cropHeight = imgHeight;
        cropWidth = cropHeight * aspectRatio;
      } else {
        // Image is taller than target ratio
        cropWidth = imgWidth;
        cropHeight = cropWidth / aspectRatio;
      }

      const cropX = (imgWidth - cropWidth) / 2;
      const cropY = (imgHeight - cropHeight) / 2;

      // Import the crop function dynamically
      const { cropAndCompressImage } = await import("@/lib/imageCompression");
      
      // Crop and compress the image
      const croppedBlob = await cropAndCompressImage(
        imageSrc,
        { x: cropX, y: cropY, width: cropWidth, height: cropHeight },
        2560,
        2560,
        0.95
      );

      // Compress for display
      const compressedBase64 = await compressImageForDisplay(
        new File([croppedBlob], "snap.jpg", { type: "image/jpeg" })
      );

      const base64Data = compressedBase64.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const compressedBlob = new Blob([byteArray], { type: "image/jpeg" });

      setCroppedImageBlob(compressedBlob);
      setPreviewUrl(URL.createObjectURL(compressedBlob));
    } catch (error) {
      console.error("Failed to auto-crop image:", error);
      toast.error(t('notifications.toast.failed_to_process_image'));
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t('notifications.toast.please_upload_an_image_file'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('notifications.toast.image_must_be_less_than_10mb'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result as string);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setShowCropDialog(false);
    setSelectedImage(null);

    try {
      const compressedBase64 = await compressImageForDisplay(
        new File([croppedBlob], "snap.jpg", { type: "image/jpeg" })
      );

      const base64Data = compressedBase64.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const compressedBlob = new Blob([byteArray], { type: "image/jpeg" });

      setCroppedImageBlob(compressedBlob);
      setPreviewUrl(URL.createObjectURL(compressedBlob));
    } catch (error) {
      console.error("Failed to compress image:", error);
      toast.error(t('notifications.toast.failed_to_process_image'));
    }
  };

  const handleRemoveImage = () => {
    setCroppedImageBlob(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handlePost = async () => {
    // Check if user is premium OR has not used their one free snap ever
    if (!isPremium) {
      // Free users get 1 progress pic total (not per month)
      if (totalSnapCount !== undefined && totalSnapCount >= 1) {
        toast.error(t('snap_dialog.free_limit'));
        navigate("/upgrade");
        return;
      }
    }

    if (!croppedImageBlob && !videoBlob) {
      toast.error(t('notifications.toast.please_add_an_image'));
      return;
    }

    if (!selectedMetric) {
      toast.error(t('notifications.toast.please_select_a_metric'));
      return;
    }

    const today = new Date();
    const selected = new Date(selectedDate);
    
    if (
      selected.getFullYear() !== today.getFullYear() ||
      selected.getMonth() !== today.getMonth() ||
      selected.getDate() !== today.getDate()
    ) {
      toast.error(t('notifications.toast.progress_snaps_can_only_be_created_for_today'));
      return;
    }

    setIsPosting(true);

    try {
      const snapDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

      const { data: dailySnaps, error: countError } = await (supabase as any)
        .from("progress_snaps")
        .select("id", { count: "exact", head: false })
        .eq("user_id", user.id)
        .eq("snap_date", snapDate);

      if (countError) throw countError;

      if (dailySnaps && dailySnaps.length >= 10) {
        toast.error(t('notifications.toast.maximum_10_snaps_per_day_reached'));
        setIsPosting(false);
        return;
      }

      const { data: existingSnap } = await (supabase as any)
        .from("progress_snaps")
        .select("id")
        .eq("user_id", user.id)
        .eq("snap_date", snapDate)
        .eq("metric_name", selectedMetric)
        .maybeSingle();

      if (existingSnap) {
        toast.error(t('notifications.toast.you_already_have_a_progress_snap_for_this_metric_t'));
        setIsPosting(false);
        return;
      }

      let imageUrl: string | null = null;
      let videoUrl: string | null = null;

      if (videoBlob) {
        // Upload video
        // Strip codec params from MIME type (e.g. "video/webm;codecs=vp9" → "video/webm")
        // Supabase storage does exact MIME type matching against allowed_mime_types
        const baseContentType = videoBlob.type.split(';')[0].trim() || 'video/webm';
        const videoExt = baseContentType.includes('mp4') ? 'mp4' : 'webm';
        const videoFileName = `${user.id}/${Date.now()}_video.${videoExt}`;
        const { error: videoUploadError } = await supabase.storage
          .from('progress-snaps')
          .upload(videoFileName, videoBlob, {
            contentType: baseContentType,
            upsert: false,
          });
        if (videoUploadError) throw videoUploadError;
        const { data: videoUrlData } = supabase.storage
          .from('progress-snaps')
          .getPublicUrl(videoFileName);
        videoUrl = videoUrlData.publicUrl;

        // Generate thumbnail from first frame (with timeouts to prevent hanging)
        try {
          const thumbCanvas = document.createElement('canvas');
          const tempVideo = document.createElement('video');
          tempVideo.muted = true;
          tempVideo.playsInline = true;
          tempVideo.preload = 'auto';
          const thumbSrc = videoPreviewUrl || URL.createObjectURL(videoBlob);
          tempVideo.src = thumbSrc;

          await withTimeout(
            new Promise<void>((res, rej) => {
              tempVideo.onloadeddata = () => res();
              tempVideo.onerror = () => rej(new Error('thumb video load error'));
            }),
            5000,
            'video load'
          );
          tempVideo.currentTime = 0.1;
          await withTimeout(
            new Promise<void>(res => { tempVideo.onseeked = () => res(); }),
            3000,
            'video seek'
          );
          thumbCanvas.width = tempVideo.videoWidth;
          thumbCanvas.height = tempVideo.videoHeight;
          thumbCanvas.getContext('2d')!.drawImage(tempVideo, 0, 0);
          const thumbBlob = await withTimeout(
            new Promise<Blob>((res, rej) => {
              thumbCanvas.toBlob(b => b ? res(b) : rej(new Error('thumb blob')), 'image/jpeg', 0.85);
            }),
            3000,
            'canvas toBlob'
          );
          const thumbFileName = `${user.id}/${Date.now()}_thumb.jpg`;
          await supabase.storage.from('progress-snaps').upload(thumbFileName, thumbBlob, { contentType: 'image/jpeg', upsert: false });
          const { data: thumbUrlData } = supabase.storage.from('progress-snaps').getPublicUrl(thumbFileName);
          imageUrl = thumbUrlData.publicUrl;
          // Clean up blob URL if we created one
          if (!videoPreviewUrl) URL.revokeObjectURL(thumbSrc);
        } catch (thumbErr) {
          console.warn('Failed to generate video thumbnail:', thumbErr);
        }
      } else if (croppedImageBlob) {
        const fileExt = "jpg";
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("progress-snaps")
          .upload(fileName, croppedImageBlob, {
            contentType: "image/jpeg",
            upsert: false,
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("progress-snaps")
          .getPublicUrl(fileName);

        imageUrl = urlData.publicUrl;
      }

      const { data: snapData, error: insertError } = await (supabase as any)
        .from("progress_snaps")
        .insert({
          user_id: user.id,
          metric_name: selectedMetric,
          image_url: imageUrl,
          video_url: videoUrl,
          text_content: textContent.trim() || null,
          snap_date: snapDate,
        })
        .select()
        .single();

      if (insertError) {
        if (insertError.code === '42501' || insertError.message?.includes('policy')) {
          // Check if it's the free limit error
          if (!isPremium) {
            toast.error(t('snap_dialog.free_limit'));
            navigate("/upgrade");
            setIsPosting(false);
            return;
          }
          toast.error(t('notifications.toast.progress_snaps_can_only_be_created_for_today'));
          setIsPosting(false);
          return;
        }
        throw insertError;
      }

      const createdSnapId = snapData?.id;
      console.log('[CreateSnap] Created snap with ID:', createdSnapId);

      const { error: feedError } = await (supabase as any).rpc("create_progress_snap_feed_items", {
        p_user_id: user.id,
        p_metric_name: selectedMetric,
        p_snap_date: snapDate,
      });

      if (feedError) {
        console.error("Failed to create feed items:", feedError);
      }

      try {
        await supabase.functions.invoke("send-progress-snap-notification", {
          body: {
            actor_id: user.id,
            metric_name: selectedMetric,
            snap_date: snapDate,
          },
        });
      } catch (notifError) {
        console.error("Failed to send push notifications:", notifError);
      }

      // Check if user has Discord webhook configured and post to Discord
      try {
        console.log('[CreateSnap] Checking for Discord webhook...');
        const { data: profile } = await supabase
          .from("profiles")
          .select("discord_webhook, username")
          .eq("id", user.id)
          .single();
        
        if (profile?.discord_webhook) {
          console.log('[CreateSnap] Discord webhook found, posting to Discord...');
          
          // imageUrl already contains the full public URL, no need to call getPublicUrl again
          const snapImageUrl = imageUrl || undefined;
          
          // Get the metric info for display
          const metricInfo = availableMetrics.find(m => m.key === selectedMetric);
          
          await shareToDiscord({
            webhookUrl: profile.discord_webhook,
            username: profile.username || "User",
            snapId: createdSnapId, // Use the actual snap ID
            snapImageUrl,
            metricName: metricInfo ? `${metricInfo.emoji} ${metricInfo.label}` : selectedMetric,
            message: `${profile.username} posted a new progress snap on gitfit.ai!`,
            textContent: textContent.trim() || undefined,
          });
          
          console.log('[CreateSnap] ✅ Successfully posted to Discord!');
        } else {
          console.log('[CreateSnap] No Discord webhook configured');
        }
      } catch (discordError) {
        console.error("[CreateSnap] ❌ Failed to post to Discord:", discordError);
        // Don't show error to user - Discord posting is optional
      }

      // Show success toast with action to view the snap
      hapticSuccess();
      toast.success(
        userProfile?.is_public || (followerCount || 0) > 0 
          ? t('notifications.toast.progress_snap_posted')
          : t('notifications.toast.progress_snap_saved'),
        {
          action: {
            label: t('notifications.toast.view'),
            onClick: () => navigate(`/snap/${createdSnapId}`),
          },
          duration: 5000,
        }
      );
      navigate(-1);
    } catch (error: any) {
      console.error("Failed to post progress snap:", error);
      const errMsg = error?.message || error?.error_description || String(error);
      // Provide a descriptive reason in the toast
      if (errMsg.includes('mime') || errMsg.includes('Mime') || errMsg.includes('content type') || errMsg.includes('mime_type')) {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + `: unsupported file type`);
      } else if (errMsg.includes('size') || errMsg.includes('too large') || errMsg.includes('Payload')) {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + `: file too large`);
      } else if (errMsg.includes('policy') || errMsg.includes('42501') || errMsg.includes('permission')) {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + `: permission denied`);
      } else if (errMsg.includes('duplicate') || errMsg.includes('23505') || errMsg.includes('unique')) {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + `: duplicate entry`);
      } else if (errMsg.includes('network') || errMsg.includes('fetch') || errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + `: network error – check your connection`);
      } else if (errMsg.includes('storage') || errMsg.includes('bucket')) {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + `: upload failed – ${errMsg}`);
      } else {
        toast.error(t('notifications.toast.failed_to_post_progress_snap') + ` (${errMsg})`);
      }
    } finally {
      setIsPosting(false);
    }
  };

  const selectedMetricInfo = availableMetrics.find(m => m.key === selectedMetric);

  return (
    <>
      <motion.div 
        className="fixed inset-0 bg-background z-50 flex flex-col"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
      >
        {/* Image Preview - Full Screen */}
        {(previewUrl || videoPreviewUrl) && (
          <motion.div 
            className="relative w-full h-full"
            initial={{ scale: 1.02 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.05, ease: "easeOut" }}
          >
            {videoPreviewUrl ? (
              <video
                src={videoPreviewUrl}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={previewUrl!}
                alt={t('accessibility.labels.progress_snap_preview')}
                className="w-full h-full object-cover"
              />
            )}
            
            {/* Top Left - Mini Graph with Metric Selector */}
            {selectedMetric && (
              <div className="absolute top-4 left-4 z-20">
                <div 
                  className="cursor-pointer"
                  onClick={() => setShowMetricSelector(true)}
                >
                  <MiniGraphOverlay
                    userId={user.id}
                    metricKey={selectedMetric}
                    metricEmoji={selectedMetricInfo?.emoji}
                    metricLabel={selectedMetricInfo?.label}
                    snapDate={selectedDate}
                    lightText
                  />
                </div>
                {/* Metric Name Display with Inline Dropdown */}
                <div className="relative mt-2">
                  <Select value={selectedMetric} onValueChange={setSelectedMetric}>
                    <SelectTrigger className="bg-black/30 backdrop-blur-sm border-0 text-white hover:bg-black/40 transition-colors h-auto py-1.5 px-3 text-sm font-medium">
                      <SelectValue>
                        <span className="flex items-center gap-1.5">
                          {selectedMetricInfo?.emoji} {selectedMetricInfo?.label}
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingPrefs || isLoadingCustom || isLoadingDefs ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          <MiniGridLoader className="h-4 w-4 mx-auto" />
                        </div>
                      ) : availableMetrics.length > 0 ? (
                        availableMetrics.map((metric) => {
                          const isUsed = todaySnaps?.some(snap => snap.metric_name === metric.key);
                          return (
                            <SelectItem key={metric.key} value={metric.key}>
                              <span className="flex items-center gap-2">
                                <span>{metric.emoji}</span>
                                <span>{metric.label}</span>
                                {isUsed && <span className="text-xs text-muted-foreground">(posted)</span>}
                              </span>
                            </SelectItem>
                          );
                        })
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground text-center">{t('ui.text.no_metrics_enabled_enable_metrics_first')}</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Top Right - Close Button */}
            <button
              onClick={() => navigate(-1)}
              disabled={isPosting}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-black/40"
            >
              <X className="h-5 w-5 text-white" />
            </button>
            
            {/* Text Overlay - Preview (Always visible, clickable) */}
            <div className="absolute left-4 right-4 bottom-[88px] z-30 flex flex-col gap-2 items-start">
              {/* Action Buttons - Above Caption */}
              <div className="flex items-center gap-2">
                {!videoPreviewUrl && (
                  <Button
                    size="icon"
                    className="h-9 w-9 rounded-lg bg-black/30 backdrop-blur-sm hover:bg-black/40"
                    onClick={() => {
                      if (croppedImageBlob) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          setSelectedImage(reader.result as string);
                          setShowCropDialog(true);
                        };
                        reader.readAsDataURL(croppedImageBlob);
                      }
                    }}
                  >
                    <Crop className="h-4 w-4 text-white" />
                  </Button>
                )}
                
                <Button
                  size="icon"
                  className="h-9 w-9 rounded-lg bg-black/30 backdrop-blur-sm hover:bg-black/40"
                  onClick={() => setShowVisibilityDialog(true)}
                >
                  {userProfile?.is_public ? (
                    <Eye className="h-4 w-4 text-white" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-white" />
                  )}
                </Button>
              </div>
              
              {/* Caption Section */}
              <div className="relative w-full">
                <button
                  onClick={() => setShowTextEditor(true)}
                  className="text-left w-full"
                >
                  <div className="bg-black/60 backdrop-blur-sm text-white p-4 pr-10 rounded-xl shadow-lg max-h-32 overflow-y-auto hover:bg-black/70 transition-colors">
                    {textContent ? (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {textContent}
                      </p>
                    ) : (
                      <p className="text-sm text-white/50">{t('ui.text.add_a_caption')}</p>
                    )}
                  </div>
                </button>
                {/* Pencil Icon Button */}
                <button
                  onClick={() => setShowTextEditor(true)}
                  className="absolute top-2 right-2 p-1.5 hover:bg-white/10 rounded transition-colors"
                >
                  <Edit2 className="h-3.5 w-3.5 text-white/70" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Post Button - Fixed at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-20 bg-gradient-to-t from-black/20 to-transparent pt-6">
          <Button 
            onClick={handlePost} 
            disabled={isPosting || (!croppedImageBlob && !videoBlob) || !selectedMetric}
            className="w-full h-14 text-base font-medium bg-gradient-to-r from-green-400 via-emerald-500 to-green-400 hover:from-green-500 hover:via-emerald-600 hover:to-green-500 text-white shadow-lg"
            size="lg"
          >
            {isPosting ? (
              <>
                <MiniGridLoader className="h-5 w-5 mr-2" />{t('ui.text.posting')}</>
            ) : (
              <>
                {userProfile?.is_public === false && (followerCount || 0) === 0 ? t('common.save_privately') : t('common.post')}
                <ArrowRight className="h-5 w-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </motion.div>
      
      {/* Text Editor Dialog */}
      <Dialog open={showTextEditor} onOpenChange={setShowTextEditor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ui.text.add_caption')}</DialogTitle>
            <DialogDescription>{t('ui.text.share_your_thoughts_about_this_progress_snap')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder={t('forms.placeholders.share_your_progress')}
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              maxLength={500}
              rows={6}
              className="resize-none"
            />
            <div className="text-xs text-muted-foreground text-right mt-2">
              {textContent.length}/500
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTextEditor(false)}>
              {t('common.done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      


      {/* Profile Visibility Dialog */}
      <Dialog open={showVisibilityDialog} onOpenChange={setShowVisibilityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ui.text.change_profile_visibility')}</DialogTitle>
            <DialogDescription>{t('ui.text.choose_who_can_see_your_profile_and_progress_snaps')}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div
              className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                userProfile?.is_public ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              }`}
              onClick={() => updateVisibility.mutate(true)}
            >
              <Eye className="w-5 h-5 text-muted-foreground" />
              <div>
                <Label className="font-semibold cursor-pointer">{t('ui.text.public_profile')}</Label>
                <p className="text-sm text-muted-foreground">{t('ui.text.visible_to_everyone')}</p>
              </div>
            </div>
            <div
              className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                !userProfile?.is_public ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              }`}
              onClick={() => updateVisibility.mutate(false)}
            >
              <EyeOff className="w-5 h-5 text-muted-foreground" />
              <div>
                <Label className="font-semibold cursor-pointer">{t('ui.text.private_profile')}</Label>
                <p className="text-sm text-muted-foreground">{t('ui.text.only_visible_to_followers')}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVisibilityDialog(false)}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedImage && (
        <ImageCropDialog
          open={showCropDialog}
          onOpenChange={setShowCropDialog}
          imageSrc={selectedImage}
          onCropComplete={handleCropComplete}
          aspectRatio={9 / 16}
          maxWidth={2560}
          maxHeight={2560}
          quality={0.95}
        />
      )}
    </>
  );
};

export default CreateSnap;
